'use client';

import { ComponentPropsWithoutRef, createContext, useContext } from 'react';
import { motion, useReducedMotion } from 'motion/react';

const FadeInStaggerContext = createContext(false);

const viewport = { once: true, margin: '0px 0px -200px' };

/**
 * Configuration properties for the FadeIn component.
 */
type TFadeInProps = ComponentPropsWithoutRef<typeof motion.div> & {
  /** Animation delay in seconds. */
  delay?: number;
  /** Animation duration in seconds. Defaults to 0.5. */
  duration?: number;
  /** Initial opacity before animation starts. */
  initOpacity?: number;
  /** Initial horizontal offset in pixels. */
  initX?: number;
  /** Initial vertical offset in pixels. Defaults to 24. */
  initY?: number;
};

/**
 * A component that applies a fade-in animation to its children when they enter the viewport.
 */
export function FadeIn({
  delay = 0,
  duration = 0.5,
  initX = 0,
  initY = 24,
  initOpacity = 0,
  ...rest
}: TFadeInProps) {
  const shouldReduceMotion = useReducedMotion();
  const isInStaggerGroup = useContext(FadeInStaggerContext);

  const updatedProps = {
    initial: 'hidden',
    whileInView: 'visible',
    viewport,
  };

  return (
    <motion.div
      transition={{ duration, delay }}
      variants={{
        hidden: {
          opacity: initOpacity,
          x: shouldReduceMotion ? 0 : initX,
          y: shouldReduceMotion ? 0 : initY,
        },
        visible: { opacity: 1, x: 0, y: 0 },
      }}
      {...(isInStaggerGroup ? {} : updatedProps)}
      {...rest}
    />
  );
}

/**
 * Configuration properties for the FadeInStagger component.
 */
type TFadeInStaggerProps = ComponentPropsWithoutRef<typeof motion.div> & {
  /** Whether the stagger animation should be faster. */
  faster?: boolean;
};

/**
 * A container component that staggers the fade-in animations of its children.
 */
export function FadeInStagger({
  faster = false,
  ...props
}: TFadeInStaggerProps) {
  return (
    <FadeInStaggerContext.Provider value>
      <motion.div
        initial="hidden"
        transition={{ staggerChildren: faster ? 0.12 : 0.2 }}
        viewport={viewport}
        whileInView="visible"
        {...props}
      />
    </FadeInStaggerContext.Provider>
  );
}
